import { List, Record } from "immutable";
import { v4 as uuid } from "uuid";
import { round } from "lodash";
import { customParseFloat } from "common/helpers/Math";
import { Nullable } from "common/records";
import { EstimateRecord } from "common/records/Estimate";
import { CurrencyFormat, PercentageFormat } from "common/helpers/Format";

export const estimateDiscountFromJSON = (json: Partial<IEstimateDiscountData>): EstimateDiscountRecord => {
  const record: IEstimateDiscountRecord = { ...(json as any) };

  if (!record.uuid) {
    record.uuid = uuid();
  }

  record.amount = customParseFloat(record.amount);
  record.calculated = customParseFloat(record.calculated);
  record.totalAfter = customParseFloat(record.totalAfter);
  record.limit = customParseFloat(record.limit);

  return new EstimateDiscountRecord(record);
};

/**
 * Result of applying a single discount - immutable
 */
interface DiscountResult {
  readonly calculated: number;
  readonly totalAfter: number;
}

/**
 * Pure function to calculate a single discount's effect
 * No mutations - returns new values
 */
export const performDiscount = (discount: EstimateDiscountRecord, discountableTotal: number): DiscountResult => {
  const calculated = (() => {
    switch (discount.kind) {
      case "percent":
        return round(discountableTotal * (discount.amount / 100), 2);
      case "percent_limit": {
        const percentAmount = round(discountableTotal * (discount.amount / 100), 2);
        return Math.min(discount.limit, percentAmount);
      }
      case "flat":
        return discount.amount;
      default:
        return 0;
    }
  })();

  return {
    calculated,
    totalAfter: discountableTotal - calculated,
  };
};

/**
 * Immutable accumulator for discount reduction
 */
interface DiscountAccumulator {
  readonly runningDiscountable: number;
  readonly totalDiscount: number;
  readonly updatedDiscounts: List<EstimateDiscountRecord>;
}

/**
 * Pure function to calculate all discounts
 * Uses reduce with immutable accumulator - no mutations
 */
export const calcDiscount = (
  estimate: EstimateRecord,
): {
  readonly discount: number;
  readonly discounts: List<EstimateDiscountRecord>;
  readonly running_discountable: number;
} => {
  const initialAccumulator: DiscountAccumulator = {
    runningDiscountable: estimate.discountable_total,
    totalDiscount: 0,
    updatedDiscounts: List<EstimateDiscountRecord>(),
  };

  const result = estimate.discounts.reduce(
    (acc: DiscountAccumulator, discount: EstimateDiscountRecord): DiscountAccumulator => {
      const discountResult = performDiscount(discount, acc.runningDiscountable);

      return {
        runningDiscountable: discountResult.totalAfter,
        totalDiscount: acc.totalDiscount + discountResult.calculated,
        updatedDiscounts: acc.updatedDiscounts.push(
          discount.merge({
            calculated: discountResult.calculated,
            totalAfter: discountResult.totalAfter,
          }),
        ),
      };
    },
    initialAccumulator,
  );

  return {
    discount: result.totalDiscount,
    discounts: result.updatedDiscounts,
    running_discountable: result.runningDiscountable,
  };
};

export const formatDiscount = (discount: EstimateDiscountRecord): string => {
  if (discount.kind === "flat") {
    return CurrencyFormat(discount.amount as number);
  }

  let formattedValue = PercentageFormat(discount.amount as number);

  if (discount.kind === "percent_limit") {
    formattedValue = `${formattedValue} up to ${CurrencyFormat(discount.limit as number)}`;
  }

  return formattedValue;
};

export interface IEstimateDiscountData {
  uuid: Nullable<string>;
  kind: string;
  name: string;
  amount: number;
  calculated: number;
  totalAfter: number;
  discountId: Nullable<number>;
  discountRev: string;
  limit: number;
}

export type IEstimateDiscountRecord = IEstimateDiscountData;

const defaultDiscountProps: IEstimateDiscountRecord = {
  uuid: null,
  kind: "percent",
  name: "",
  amount: 0,
  calculated: 0,
  totalAfter: 0,
  discountId: null,
  discountRev: "",
  limit: 0,
};

export class EstimateDiscountRecord extends Record(defaultDiscountProps) implements IEstimateDiscountRecord {}

export const estimateDiscountName = (discount: EstimateDiscountRecord) => {
  const amount = discount.get("amount") || 0;
  const kind = discount.get("kind");
  const discountSuffix = ["percent", "percent_limit"].includes(kind) ? ` (${amount}%)` : "";
  return `${discount.get("name")}${discountSuffix}`;
};
